import { useMemo, useState } from "react";

import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/Badge";
import { localise, proseFor } from "@/components/docs/prose";
import {
  ERRORS,
  INTEGRATION,
  OVERVIEW,
  SECTIONS,
  SOCKET,
  type Endpoint,
  type Section,
} from "@/components/docs/reference";
import { env } from "@/env";
import { cn } from "@/lib/cn";
import { useI18n, type Translate } from "@/lib/i18n";

const AUTH_TONE = { public: "success", user: "brand", service: "warning" } as const;
const AUTH_LABEL = {
  public: "docs.auth.public",
  user: "docs.auth.user",
  service: "docs.auth.service",
} as const;

const METHOD_TONE = {
  GET: "text-success",
  POST: "text-brand",
  PATCH: "text-warning",
  DELETE: "text-danger",
} as const;

const ENGLISH: Section[] = [...SECTIONS, SOCKET, INTEGRATION];

export function DocsPage() {
  const { locale, t } = useI18n();
  const [active, setActive] = useState<string>(SECTIONS[0]?.id ?? "auth");
  const prose = useMemo(() => proseFor(locale), [locale]);
  const all = useMemo(() => ENGLISH.map((entry) => localise(entry, prose)), [prose]);
  const section = all.find((entry) => entry.id === active) ?? all[0];

  return (
    <AppShell>
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        <nav className="shrink-0 lg:sticky lg:top-20 lg:w-56">
          <h1 className="text-lg font-semibold text-ink">
            {t("docs.title", { name: env.appName })}
          </h1>
          <p className="mt-1 text-xs text-ink-muted">{t("docs.version")}</p>
          <ul className="mt-4 flex flex-wrap gap-1 lg:flex-col">
            {all.map((entry) => (
              <li key={entry.id}>
                <button
                  className={cn(
                    "w-full rounded-lg px-3 py-1.5 text-start text-sm transition-colors",
                    entry.id === active
                      ? "bg-brand-soft font-medium text-brand"
                      : "text-ink-muted hover:bg-surface-raised hover:text-ink",
                  )}
                  onClick={() => {
                    setActive(entry.id);
                  }}
                  type="button"
                >
                  {entry.title}
                </button>
              </li>
            ))}
            <li>
              <button
                className={cn(
                  "w-full rounded-lg px-3 py-1.5 text-start text-sm transition-colors",
                  active === "errors"
                    ? "bg-brand-soft font-medium text-brand"
                    : "text-ink-muted hover:bg-surface-raised hover:text-ink",
                )}
                onClick={() => {
                  setActive("errors");
                }}
                type="button"
              >
                {t("docs.errors")}
              </button>
            </li>
          </ul>
        </nav>

        <article className="min-w-0 flex-1">
          {active === SECTIONS[0]?.id ? (
            <Prose className="mb-8 rounded-panel border border-line bg-surface-sunken p-5">
              {prose("overview", OVERVIEW)}
            </Prose>
          ) : null}

          {active === "errors" ? (
            <>
              <h2 className="text-xl font-semibold text-ink">{t("docs.errors")}</h2>
              <Prose className="mt-3">{prose("errors", ERRORS)}</Prose>
            </>
          ) : section === undefined ? null : (
            <SectionView section={section} t={t} />
          )}
        </article>
      </div>
    </AppShell>
  );
}

function SectionView({ section, t }: { section: Section; t: Translate }) {
  return (
    <>
      <h2 className="text-xl font-semibold text-ink">{section.title}</h2>
      <Prose className="mt-3">{section.intro}</Prose>

      {section.endpoints !== undefined ? (
        <div className="mt-6 flex flex-col gap-3">
          {section.endpoints.map((endpoint) => (
            <EndpointView endpoint={endpoint} key={`${endpoint.method} ${endpoint.path}`} t={t} />
          ))}
        </div>
      ) : null}

      {section.events !== undefined ? (
        <div className="mt-6 overflow-x-auto rounded-panel border border-line">
          <table className="w-full min-w-[44rem] border-collapse text-sm">
            <thead>
              <tr className="border-b border-line bg-surface-sunken text-start">
                <th className="px-4 py-2 text-start font-medium text-ink-muted">
                  {t("docs.event")}
                </th>
                <th className="px-4 py-2 text-start font-medium text-ink-muted">
                  {t("docs.direction")}
                </th>
                <th className="px-4 py-2 text-start font-medium text-ink-muted">
                  {t("docs.payload")}
                </th>
              </tr>
            </thead>
            <tbody>
              {section.events.map((event) => (
                <tr className="border-b border-line last:border-0 align-top" key={event.name}>
                  <td className="px-4 py-2.5">
                    <code className="font-mono text-xs text-ink" dir="ltr">
                      {event.name}
                    </code>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge
                      className="whitespace-nowrap"
                      tone={event.direction === "in" ? "brand" : "neutral"}
                    >
                      {t(event.direction === "in" ? "docs.inbound" : "docs.outbound")}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    {}
                    <code className="font-mono text-xs break-words text-ink-muted" dir="ltr">
                      {event.payload}
                    </code>
                    <p className="mt-1 text-xs text-ink-muted">{event.summary}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {section.code?.map((block) => (
        <figure className="mt-6" key={block.caption}>
          <figcaption className="mb-2 text-sm font-medium text-ink">{block.caption}</figcaption>
          <pre
            className="overflow-x-auto rounded-panel border border-line bg-surface-sunken p-4 text-xs leading-relaxed"
            dir="ltr"
          >
            <code className="font-mono text-ink">{block.source}</code>
          </pre>
        </figure>
      ))}
    </>
  );
}

function EndpointView({ endpoint, t }: { endpoint: Endpoint; t: Translate }) {
  return (
    <section className="rounded-panel border border-line bg-surface p-4">
      <p className="flex flex-wrap items-baseline gap-2">
        {}
        <span className="flex flex-wrap items-baseline gap-2" dir="ltr">
          <span className={cn("font-mono text-xs font-semibold", METHOD_TONE[endpoint.method])}>
            {endpoint.method}
          </span>
          <code className="font-mono text-sm break-all text-ink">{endpoint.path}</code>
        </span>
        <Badge className="ms-auto" tone={AUTH_TONE[endpoint.auth]}>
          {t(AUTH_LABEL[endpoint.auth])}
        </Badge>
      </p>
      <p className="mt-1.5 text-sm text-ink-muted">{endpoint.summary}</p>

      <dl className="mt-3 grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-[6rem_1fr]">
        {endpoint.body !== undefined ? (
          <>
            <dt className="text-ink-faint">{t("docs.body")}</dt>
            <dd>
              <code className="font-mono break-all text-ink-muted" dir="ltr">
                {endpoint.body}
              </code>
            </dd>
          </>
        ) : null}
        <dt className="text-ink-faint">{t("docs.returns")}</dt>
        <dd>
          <code className="font-mono break-all text-ink-muted" dir="ltr">
            {endpoint.returns}
          </code>
        </dd>
        {endpoint.notes !== undefined ? (
          <>
            <dt className="text-ink-faint">{t("docs.note")}</dt>
            <dd className="text-ink-muted">{endpoint.notes}</dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}

function splitCode(block: string): { id: string; code: boolean; text: string }[] {
  const parts: { id: string; code: boolean; text: string }[] = [];
  let cursor = 0;
  for (const run of block.split(/(`[^`]+`)/u)) {
    if (run !== "") {
      const code = run.startsWith("`");
      parts.push({ id: `${cursor}`, code, text: code ? run.slice(1, -1) : run });
    }
    cursor += run.length;
  }
  return parts;
}

function Prose({ children, className }: { children: string; className?: string }) {
  const blocks = children.split(/\n{2,}/u);
  return (
    <div className={cn("flex flex-col gap-3 text-sm leading-relaxed text-ink-muted", className)}>
      {blocks.map((block) =>
        block.startsWith("    ") ? (
          <pre
            className="overflow-x-auto rounded-xl border border-line bg-surface-sunken p-3 text-xs"
            dir="ltr"
            key={block}
          >
            <code className="font-mono text-ink">{block.replaceAll(/^ {4}/gmu, "")}</code>
          </pre>
        ) : (
          <p key={block}>
            {splitCode(block).map((part) =>
              part.code ? (
                <code
                  className="rounded bg-surface-sunken px-1 py-0.5 font-mono text-xs text-ink"
                  dir="ltr"
                  key={part.id}
                >
                  {part.text}
                </code>
              ) : (
                part.text
              ),
            )}
          </p>
        ),
      )}
    </div>
  );
}
